import type { ServicePrice } from '@/features/payments';

import type { TierConfig } from '../constants/tier-config';

/**
 * Placeholder rendered whenever a price is still loading or cannot be resolved.
 * Centralised so the UI is consistent and a future change (skeleton, dash, etc.)
 * only needs to happen here.
 */
export const PRICE_LOADING_PLACEHOLDER = '…';

/**
 * Look up the tier's Pricing row and return its price in pence.
 *
 * Returns `null` when pricing hasn't loaded yet, OR the tier's row is missing /
 * inactive in the response. Callers MUST treat `null` as "price unknown" and
 * disable purchase — never substitute 0 (would let `balance >= 0` pass).
 *
 * The price returned here is the FLAT TIER FEE the user pays at selection. It
 * is not derived from summing service rows — see TIER_INCLUDED_SERVICES on the
 * backend for what each tier unlocks.
 */
export function getTierPricePence(
  tier: TierConfig,
  servicePrices: ServicePrice[] | undefined,
): number | null {
  if (!servicePrices) return null;
  const row = servicePrices.find((p) => p.key === tier.tierKey);
  if (!row) return null;
  if (!Number.isFinite(row.pricePence)) return null;
  return row.pricePence;
}

export function formatTierPrice(pricePence: number | null): string {
  if (pricePence === null) return PRICE_LOADING_PLACEHOLDER;
  return `£${(pricePence / 100).toFixed(2)}`;
}
